/**
 * Esquemas de las entidades sincronizables. El mismo esquema valida los datos
 * en el cliente (antes de guardar), en el servidor (tras fusionar) y en la
 * importación de archivos.
 */
import { z } from 'zod';
import { isValidLocalDate, isValidTimeZone, validateHabit, type HabitDef } from '@habitos/engine';

/** Entidades de la 0.2: un cliente que no declara las que admite solo recibe estas al sincronizar. */
export const LEGACY_ENTITIES = ['settings', 'habits', 'entries', 'overrides', 'groups', 'vacations', 'reminders'] as const;
export const ENTITIES = [...LEGACY_ENTITIES, 'notes', 'listItems', 'journal', 'focus', 'moves'] as const;
export type EntityName = (typeof ENTITIES)[number];

const localDate = z.string().refine(isValidLocalDate, 'fecha no válida');
const nullableDate = localDate.nullable();
const isoInstant = z.string().datetime({ offset: true });
const tz = z.string().refine(isValidTimeZone, 'zona horaria no válida');
const shortText = (max: number) => z.string().max(max);
export const idSchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'identificador no válido');
const deletedAt = isoInstant.nullable().default(null);
const weekday = z.number().int().min(1).max(7);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** Solo enlaces http(s): nunca javascript:, data:, file:… */
const webUrl = z
  .string()
  .max(2000)
  .regex(/^https?:\/\/[^\s/?#]+[^\s]*$/i, 'url no válida');
const tag = z.string().trim().min(1).max(30);

export const scheduleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('daily') }),
  z.object({ type: z.literal('weekdays'), days: z.array(weekday).min(1).max(7), everyWeeks: z.number().int().min(1).max(12).optional() }),
  z.object({ type: z.literal('weekly_quota'), mode: z.enum(['times', 'amount']), times: z.number().int().min(1).max(7).optional() }),
  z.object({
    type: z.literal('monthdays'),
    days: z.array(z.number().int().min(1).max(31)).max(31),
    lastDay: z.boolean().optional(),
  }),
  z.object({ type: z.literal('monthly_quota'), mode: z.enum(['times', 'amount']), times: z.number().int().min(1).max(31).optional() }),
  z.object({ type: z.literal('interval_days'), every: z.number().int().min(1).max(365) }),
  z.object({ type: z.literal('yearly') }),
]);

export const habitVersionSchema = z.object({
  effectiveFrom: localDate,
  goalType: z.enum(['binary', 'numeric']),
  target: z.number().finite().min(0).max(1_000_000),
  unit: shortText(24).nullable().optional(),
  schedule: scheduleSchema,
  average: z.number().int().min(2).max(30).nullable().optional(),
});

export const settingsSchema = z.object({
  dayStartMinutes: z.number().int().min(0).max(1439),
  weekStartInitial: weekday,
  weekStartChanges: z.array(z.object({ changedOn: localDate, weekStart: weekday })).max(200),
  tzMode: z.enum(['follow_device', 'fixed']),
  homeTz: tz,
  effectiveTz: tz,
  hideStreaks: z.boolean(),
  quietStart: z.number().int().min(0).max(1439).nullable(),
  quietEnd: z.number().int().min(0).max(1439).nullable(),
});

export const habitSchema = z
  .object({
    kind: z.enum(['build', 'quit', 'task']),
    name: z.string().trim().min(1).max(80),
    description: shortText(500).nullable(),
    icon: shortText(32),
    color: shortText(24),
    startDate: localDate,
    endDate: nullableDate,
    dueDate: nullableDate,
    completedAt: isoInstant.nullable(),
    archivedAt: nullableDate,
    sortKey: shortText(64),
    primaryGroupId: idSchema.nullable(),
    secondaryGroupIds: z.array(idSchema).max(20),
    hideStreak: z.boolean(),
    defaultIncrement: z.number().finite().positive().max(100_000),
    versions: z.array(habitVersionSchema).max(200),
    createdAt: isoInstant,
    deletedAt,
    /** Hora opcional de una tarea (0.3). Los registros anteriores la reciben como null. */
    dueTime: hhmm.nullable().default(null),
    /** Duración prevista en minutos (0.4), para la agenda y los conflictos. Los registros anteriores la reciben como null. */
    durationMinutes: z.number().int().min(5).max(720).nullable().default(null),
    /** Fuera del progreso del día (0.6): se sigue registrando, pero no entra en el porcentaje. Los registros anteriores lo reciben como false. */
    excludedFromProgress: z.boolean().default(false),
  })
  .superRefine((h, ctx) => {
    const def: HabitDef = { id: 'x', ...h };
    for (const code of validateHabit(def)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: code });
  });

export const entrySchema = z.object({
  habitId: idSchema,
  logicalDate: localDate,
  value: z.number().finite().min(-1_000_000).max(1_000_000),
  occurredAt: isoInstant,
  tz,
  source: z.enum(['manual', 'timer', 'import', 'health']),
  note: shortText(1000).nullable(),
  createdAt: isoInstant,
  deletedAt,
});

export const overrideSchema = z.object({
  habitId: idSchema,
  ueKey: z.string().regex(/^(D:\d{4}-\d{2}-\d{2}|W:\d{4}-\d{2}-\d{2}|M:\d{4}-\d{2})$/),
  kind: z.enum(['skipped', 'failed']),
  reason: shortText(200).nullable(),
  deletedAt,
});

export const groupSchema = z.object({
  name: z.string().trim().min(1).max(40),
  color: shortText(24),
  sortKey: shortText(64),
  deletedAt,
});

export const vacationSchema = z
  .object({
    name: shortText(60).nullable(),
    startDate: localDate,
    endDate: localDate,
    scope: z.enum(['all', 'selected']),
    habitIds: z.array(idSchema).max(500),
    deletedAt,
  })
  .refine((v) => v.endDate >= v.startDate, 'end_before_start');

/**
 * Traslado de una ocurrencia a otro día (FR-SCH-12). El identificador es determinista
 * (habitId + fromDate), así que trasladar dos veces el mismo día desde dos dispositivos
 * converge en un solo registro en vez de duplicarse.
 */
export const moveSchema = z
  .object({
    habitId: idSchema,
    fromDate: localDate,
    toDate: localDate,
    deletedAt,
  })
  .refine((m) => m.fromDate !== m.toDate, 'same_day');

export const noteSchema = z.object({
  /** quote, fact y contact: categorías del Cerebro añadidas en 0.5 (el autor o los datos van en body). */
  kind: z.enum(['note', 'idea', 'link', 'date', 'list', 'quote', 'fact', 'contact']),
  title: z.string().trim().min(1).max(120),
  body: shortText(20_000).nullable(),
  url: webUrl.nullable(),
  date: nullableDate,
  tags: z.array(tag).max(20),
  /** Hábitos o tareas vinculados (ambos son registros de habits). */
  habitIds: z.array(idSchema).max(20),
  pinned: z.boolean(),
  createdAt: isoInstant,
  deletedAt,
});

/**
 * Elemento de una lista (nota de tipo list). Cada elemento es su propio registro: la
 * fusión por campo hace que dos dispositivos que añaden elementos sin conexión no se
 * pisen (con un array dentro de la nota, uno de los dos cambios se perdería).
 */
export const listItemSchema = z.object({
  listId: idSchema,
  text: z.string().trim().min(1).max(200),
  done: z.boolean(),
  sortKey: shortText(64),
  createdAt: isoInstant,
  deletedAt,
});

export const journalSchema = z.object({
  date: localDate,
  text: shortText(20_000),
  prompts: z.array(z.object({ key: z.string().min(1).max(40), answer: shortText(4000) })).max(10),
  /** Solo las entradas que el usuario marca aparecen en la revisión semanal. */
  includeInReview: z.boolean(),
  createdAt: isoInstant,
  deletedAt,
});

export const focusSchema = z.object({
  habitId: idSchema.nullable(),
  label: shortText(80).nullable(),
  plannedSeconds: z.number().int().min(60).max(180 * 60),
  activeSeconds: z.number().int().min(0).max(24 * 3600),
  startedAt: isoInstant,
  endedAt: isoInstant,
  logicalDate: localDate,
  completed: z.boolean(),
  deletedAt,
});

export const reminderSchema = z.object({
  habitId: idSchema,
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  days: z.array(weekday).max(7).nullable(),
  enabled: z.boolean(),
  deletedAt,
});

export const entitySchemas = {
  settings: settingsSchema,
  habits: habitSchema,
  entries: entrySchema,
  overrides: overrideSchema,
  groups: groupSchema,
  vacations: vacationSchema,
  reminders: reminderSchema,
  notes: noteSchema,
  listItems: listItemSchema,
  journal: journalSchema,
  focus: focusSchema,
  moves: moveSchema,
} as const;

export type Settings = z.infer<typeof settingsSchema>;
export type Habit = z.infer<typeof habitSchema>;
export type Entry = z.infer<typeof entrySchema>;
export type Override = z.infer<typeof overrideSchema>;
export type Group = z.infer<typeof groupSchema>;
export type Vacation = z.infer<typeof vacationSchema>;
export type Reminder = z.infer<typeof reminderSchema>;
export type Note = z.infer<typeof noteSchema>;
export type ListItem = z.infer<typeof listItemSchema>;
export type Journal = z.infer<typeof journalSchema>;
export type Focus = z.infer<typeof focusSchema>;
export type Move = z.infer<typeof moveSchema>;

export interface EntityTypes {
  settings: Settings;
  habits: Habit;
  entries: Entry;
  overrides: Override;
  groups: Group;
  vacations: Vacation;
  reminders: Reminder;
  notes: Note;
  listItems: ListItem;
  journal: Journal;
  focus: Focus;
  moves: Move;
}

export const SETTINGS_ID = 'settings-singleton';

/** Identificador determinista de una anulación (FR-SYNC-06). */
export function overrideId(habitId: string, ueKey: string): string {
  const key = ueKey.replace(/[:]/g, '_');
  return `${habitId}_${key}`.slice(0, 64);
}

/** Identificador determinista de un traslado: un día de origen solo puede trasladarse una vez. */
export function moveId(habitId: string, fromDate: string): string {
  return `${habitId}_m_${fromDate.replace(/-/g, '')}`.slice(0, 64);
}

export function isValidEntityId(entity: EntityName, id: string): boolean {
  if (entity === 'settings') return id === SETTINGS_ID;
  return idSchema.safeParse(id).success;
}
